import {
    IonAlert,
    IonBackButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonSpinner,
    IonTitle,
    IonToast,
    IonToolbar,
    RefresherEventDetail,
    useIonRouter
} from "@ionic/react";
import {
    lockClosedOutline,
    personRemoveOutline,
    refreshOutline,
    shirtOutline
} from "ionicons/icons";
import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from "react";

import {
    useHistory,
    useLocation,
    useParams
} from "react-router-dom";

import Cookies from "js-cookie";

import "./FantasyDropPlayers.css";
import FantasyPlayerModal from "../components/FantasyPlayerModal";


interface FantasyRosterPlayer {
    id: string;
    name: string;
    positionId: string;
    position: string;
    teamId: string;
    teamName: string;
    slot: string;
    isLocked: boolean;
    lockReason?: string;
}

interface FantasyDropState {
    success: boolean;
    message?: string;

    league: {
        id: string;
        name: string;
    };

    draft: {
        completed: boolean;
        message?: string;
    };

    week: {
        realRound: number;
        fantasyRound: number;
        status: string;
    };

    roster: {
        count: number;
        maxPlayers: number;
        players: FantasyRosterPlayer[];
    };
}

const apiBaseUrl =
    "https://fantasy.ftblscore.com/create_league";

function getAuthHeaders(): HeadersInit {
    return {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Access-Username":
            Cookies.get("access_username") || "",
        "X-Access-Token":
            Cookies.get("access_token") || ""
    };
}

function getPositionLabel(
    player: Pick<
        FantasyRosterPlayer,
        "position" | "positionId"
    >
): string {
    const position = String(player.position || "")
        .trim()
        .toUpperCase();

    if (position !== "") {
        return position;
    }

    const positions: Record<string, string> = {
        "1": "QB",
        "2": "RB",
        "3": "WR",
        "7": "K",
        "8": "DEF"
    };

    return positions[String(player.positionId || "")] || "—";
}

function getWeekStatusLabel(status?: string): string {
    const normalizedStatus = String(status || "")
        .trim()
        .toLowerCase();

    if (normalizedStatus === "live") {
        return "En vivo";
    }

    if (normalizedStatus === "scheduled") {
        return "Próximamente";
    }

    if (normalizedStatus === "final") {
        return "Finalizada";
    }

    return "Sin información";
}

function escapeHtml(value: string): string {
    return value.replace(
        /[&<>"']/g,
        function (character) {
            const characters: Record<string, string> = {
                "&": "&amp;",
                "<": "&lt;",
                ">": "&gt;",
                '"': "&quot;",
                "'": "&#039;"
            };

            return characters[character];
        }
    );
}

const FantasyDropPlayers: React.FC = () => {
    const router = useIonRouter();
    const history = useHistory();
    const location = useLocation();

    const { id } = useParams<{ id: string }>();

    const [state, setState] =
        useState<FantasyDropState | null>(null);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [message, setMessage] = useState("");

    const [
        selectedPlayer,
        setSelectedPlayer
    ] = useState<FantasyRosterPlayer | null>(null);

    const [
    selectedFantasyPlayerId,
    setSelectedFantasyPlayerId
] = useState<string | null>(null);

const requestedPlayerId = useMemo(
    function (): string {
        const params = new URLSearchParams(
            location.search
        );

        return params.get("playerId") || "";
    },
    [location.search]
);

    function isAuthenticated(): boolean {
        return Boolean(
            Cookies.get("access_token") &&
            Cookies.get("access_username")
        );
    }

    function redirectToLogin() {
        router.push("/home", "root", "replace");
    }

function goToLeagueHome(): void {
    if (!id) {
        return;
    }

    history.replace(`/fantasyLeague/${id}`);
}


    function goToPlayers() {
        router.push(
            `/fantasyPlayers/${id}`,
            "back",
            "replace"
        );
    }

    function goToFantasyTeam() {
        router.push(
            `/fantasyTeam/${id}`,
            "forward",
            "push"
        );
    }

    function goToFantasyGame() {
        router.push(
            `/fantasyGame/${id}`,
            "forward",
            "push"
        );
    }

    const loadPlayers = useCallback(
        async function (forceRefresh = false) {
            if (!isAuthenticated()) {
                redirectToLogin();
                return;
            }

            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const response = await fetch(
                    `${apiBaseUrl}/control_FantasyPlayersState.php` +
                        `?id=${encodeURIComponent(id)}` +
                        `&t=${Date.now()}`,
                    {
                        method: "GET",
                        credentials: "include",
                        headers: {
                            Accept: "application/json",
                            "X-Access-Username":
                                Cookies.get(
                                    "access_username"
                                ) || "",
                            "X-Access-Token":
                                Cookies.get(
                                    "access_token"
                                ) || ""
                        },
                        cache: "no-store"
                    }
                );

                const rawResponse = await response.text();

                let result: FantasyDropState;

                try {
                    result = JSON.parse(rawResponse);
                } catch {
                    throw new Error(
                        rawResponse.trim()
                            ? `El servidor devolvió una respuesta no válida. HTTP ${response.status}.`
                            : `El servidor devolvió una respuesta vacía. HTTP ${response.status}.`
                    );
                }

                if (!response.ok || !result.success) {
                    throw new Error(
                        result.message ||
                            "No fue posible cargar tu roster."
                    );
                }

                setState(result);
            } catch (requestError) {
                console.error(
                    "No fue posible cargar FantasyDropPlayers:",
                    requestError
                );

                setError(
                    requestError instanceof Error
                        ? requestError.message
                        : "No fue posible cargar tu roster."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id, router]
    );

    useEffect(
        function () {
            loadPlayers();
        },
        [loadPlayers]
    );

    useEffect(
    function () {
        if (!state || !requestedPlayerId) {
            return;
        }

        const playerExists =
            state.roster.players.some(
                function (player) {
                    return player.id === requestedPlayerId;
                }
            );

        if (!playerExists) {
            setMessage(
                "El jugador ya no forma parte de tu roster."
            );

            history.replace(
                `/fantasyDropPlayers/${id}`
            );

            return;
        }

        setSelectedFantasyPlayerId(
            requestedPlayerId
        );

        /*
            Evita que se vuelva a abrir la ficha
            tras actualizar la pantalla.
        */
        history.replace(
            `/fantasyDropPlayers/${id}`
        );
    },
    [
        state,
        requestedPlayerId,
        history,
        id
    ]
);

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadPlayers(true);
        event.detail.complete();
    }

    function requestDrop(player: FantasyRosterPlayer) {
        if (!state?.draft.completed) {
            setMessage(
                state?.draft.message ||
                    "No puedes hacer movimientos hasta que finalice el draft."
            );

            return;
        }

        if (player.isLocked) {
            setMessage(
                player.lockReason ||
                    "No puedes soltar este jugador porque su partido ya inició."
            );

            return;
        }

        setSelectedPlayer(player);
    }

    async function confirmDrop() {
        if (!selectedPlayer || saving) {
            return;
        }

        try {
            setSaving(true);

            const response = await fetch(
                `${apiBaseUrl}/control_DropFantasyPlayer.php`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                        leagueId: id,
                        playerId: selectedPlayer.id
                    })
                }
            );

            const rawResponse = await response.text();

            let result: {
                success?: boolean;
                message?: string;
            };

            try {
                result = JSON.parse(rawResponse);
            } catch {
                throw new Error(
                    rawResponse.trim()
                        ? `El servidor devolvió una respuesta no válida. HTTP ${response.status}.`
                        : `El servidor devolvió una respuesta vacía. HTTP ${response.status}.`
                );
            }

            if (!response.ok || !result.success) {
                throw new Error(
                    result.message ||
                        "No fue posible soltar al jugador."
                );
            }

            const droppedPlayerName = selectedPlayer.name;

            setSelectedPlayer(null);

            setMessage(
                result.message ||
                    `${droppedPlayerName} fue liberado correctamente.`
            );

            await loadPlayers(true);
        } catch (requestError) {
            console.error(
                "No fue posible soltar jugador Fantasy:",
                requestError
            );

            setMessage(
                requestError instanceof Error
                    ? requestError.message
                    : "No fue posible soltar al jugador."
            );
        } finally {
            setSaving(false);
        }
    }

    const rosterPlayers = [...(state?.roster.players || [])]
        .sort(function (leftPlayer, rightPlayer) {
            return leftPlayer.name.localeCompare(
                rightPlayer.name,
                "es"
            );
        });
async function handleDropFromPlayerModal(
    playerId: string
) {
    const player = state?.roster.players.find(
        function (rosterPlayer) {
            return rosterPlayer.id === playerId;
        }
    );

    if (!player) {
        setMessage(
            "El jugador ya no forma parte de tu roster."
        );

        setSelectedFantasyPlayerId(null);

        await loadPlayers(true);

        return;
    }

    /*
        Cerramos la ficha del jugador antes de abrir el
        diálogo de confirmación de baja.
    */
    setSelectedFantasyPlayerId(null);

    requestDrop(player);
}

const activePlayersSubmenu = useMemo(
    function (): string {
        if (
            location.pathname.includes(
                "/fantasyDropPlayers/"
            )
        ) {
            return "drop";
        }

        if (
            location.pathname.includes(
                "/fantasyTopPlayers/"
            )
        ) {
            return "leaders";
        }

        return "add";
    },
    [location.pathname]
);
    return (
        <IonPage>
            <IonHeader translucent>
<IonToolbar className="fantasy-drop-toolbar">
    <IonButtons slot="start">
        <IonBackButton
            defaultHref={`/fantasyPlayers/${id}`}
            text=""
        />
    </IonButtons>

    <IonTitle>Jugadores</IonTitle>
</IonToolbar>


                <IonToolbar className="fantasy-drop-navigation">

                    <IonSegment
                        value="players"
                        onIonChange={function (event) {
                            const selectedTab =
                                event.detail.value;

                            if (selectedTab === "home") {
                                goToLeagueHome();
                                return;
                            }

                            if (selectedTab === "team") {
                                goToFantasyTeam();
                                return;
                            }

                            if (selectedTab === "game") {
                                goToFantasyGame();
                            }
                        }}
                    >
                        <IonSegmentButton value="home">
                            Inicio
                        </IonSegmentButton>

                        <IonSegmentButton value="players">
                            Jugadores
                        </IonSegmentButton>

                        <IonSegmentButton value="team">
                            Mi equipo
                        </IonSegmentButton>

                        <IonSegmentButton value="game">
                            Juego
                        </IonSegmentButton>
                    </IonSegment>
                </IonToolbar>

                <IonToolbar className="fantasy-drop-subnavigation fantasy-players-subnavigation">
    <IonSegment
        value={activePlayersSubmenu}
        onIonChange={function (event) {
            const selectedTab = String(
                event.detail.value || "add"
            );

            if (selectedTab === activePlayersSubmenu) {
                return;
            }

            if (selectedTab === "add") {
                history.replace(
                    `/fantasyPlayers/${id}`
                );

                return;
            }

            if (selectedTab === "drop") {
                history.replace(
                    `/fantasyDropPlayers/${id}`
                );

                return;
            }

            if (selectedTab === "leaders") {
                history.replace(
                    `/fantasyTopPlayers/${id}`
                );
            }
        }}
    >
        <IonSegmentButton value="add">
            Agregar
        </IonSegmentButton>

        <IonSegmentButton value="drop">
            Soltar
        </IonSegmentButton>

        <IonSegmentButton value="leaders">
            Líderes
        </IonSegmentButton>
    </IonSegment>
</IonToolbar>
            </IonHeader>

            <IonContent
    fullscreen
    className="fantasy-drop-content"
>

                <IonRefresher
                    slot="fixed"
                    onIonRefresh={handleRefresh}
                >
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando roster..."
                    />
                </IonRefresher>

                {loading && (
                    <IonProgressBar
                        type="indeterminate"
                        className="fantasy-players-progress"
                    />
                )}

                {!loading && error && (
                    <main className="fantasy-players-main">
                        <div className="fantasy-players-empty">
                            <IonIcon icon={refreshOutline} />

                            <strong>
                                No pudimos cargar tu roster
                            </strong>

                            <span>{error}</span>

                            <IonButton
                                size="small"
                                fill="outline"
                                onClick={function () {
                                    loadPlayers(true);
                                }}
                            >
                                Reintentar
                            </IonButton>
                        </div>
                    </main>
                )}

                {!loading && !error && state && (
                    <><section className="fantasy-drop-hero">
                        <div className="fantasy-drop-hero__icon">
                            <IonIcon
                                icon={personRemoveOutline} />
                        </div>

                        <div>
                            <span>{state.league.name}</span>

                            <h1>Soltar jugadores</h1>

                            <p>
                                Libera jugadores que no necesites
                                para abrir espacio en tu roster.
                            </p>
                        </div>
                    </section>
                    <main className="fantasy-drop-main">
                    <section className="fantasy-players-summary">
                            <div>
                                <span>ROSTER</span>

                                <strong>
                                    {state.roster.count} /{" "}
                                    {state.roster.maxPlayers}
                                </strong>
                            </div>

                            <div>
                                <span>JORNADA</span>

                                <strong>
                                    {state.week.realRound}
                                </strong>
                            </div>

                            <div>
                                <span>ESTADO</span>

                                <strong>
                                    {getWeekStatusLabel(
                                        state.week.status
                                    )}
                                </strong>
                            </div>
                        </section>

                            {!state.draft.completed && (
                                <section className="fantasy-players-notice">
                                    <IonIcon
                                        icon={lockClosedOutline} />

                                    <div>
                                        <strong>
                                            Movimientos bloqueados
                                        </strong>

                                        <span>
                                            {state.draft.message ||
                                                "No puedes hacer movimientos hasta que finalice el draft."}
                                        </span>
                                    </div>
                                </section>
                            )}

                            <section className="fantasy-players-section">
                                <div className="fantasy-players-section__heading">
                                    <div>
                                        <span>MI ROSTER</span>

                                        <h2>
                                            Jugadores (
                                            {rosterPlayers.length})
                                        </h2>
                                    </div>

                                    {refreshing && (
                                        <IonSpinner name="crescent" />
                                    )}
                                </div>

                                {rosterPlayers.length === 0 ? (
                                    <div className="fantasy-players-empty">
                                        <IonIcon icon={shirtOutline} />

                                        <strong>
                                            Tu roster está vacío
                                        </strong>

                                        <span>
                                            Agrega jugadores desde la
                                            sección de agencia libre.
                                        </span>

                                        <IonButton
                                            size="small"
                                            onClick={goToPlayers}
                                        >
                                            Agregar jugadores
                                        </IonButton>
                                    </div>
                                ) : (
                                    <div className="fantasy-players-list">
                                        {rosterPlayers.map(
                                            function (player) {
                                                const position = getPositionLabel(
                                                    player
                                                );

                                                return (
                                                    <article
    key={player.id}
    className={[
        "fantasy-players-card",
        "fantasy-players-card--selectable",
        player.isLocked
            ? "fantasy-players-card--locked"
            : ""
    ].join(" ")}
    onClick={function () {
        setSelectedFantasyPlayerId(player.id);
    }}
>

                                                        <span
                                                            className={`fantasy-players-position fantasy-players-position--${position.toLowerCase()}`}
                                                        >
                                                            {position}
                                                        </span>

                                                        <div className="fantasy-players-card__details">
                                                            <strong>
                                                                {player.name}
                                                            </strong>

                                                            <small>
                                                                {player.teamName ||
                                                                    "Sin equipo"}{" "}
                                                                ·{" "}
                                                                {player.slot ||
                                                                    "BN"}
                                                            </small>

                                                            {player.isLocked && (
                                                                <em>
                                                                    <IonIcon
                                                                        icon={lockClosedOutline} />

                                                                    {player.lockReason ||
                                                                        "Partido iniciado"}
                                                                </em>
                                                            )}
                                                        </div>

                                                        <IonButton
                                                            size="small"
                                                            disabled={!state
                                                                .draft
                                                                .completed ||
                                                                player.isLocked ||
                                                                saving}
                                                            onClick={function (event) {
    event.stopPropagation();

    requestDrop(player);
}}

                                                        >
                                                            <IonIcon
                                                                slot="start"
                                                                icon={personRemoveOutline} />

                                                            Soltar
                                                        </IonButton>
                                                    </article>
                                                );
                                            }
                                        )}
                                    </div>
                                )}
                            </section>
                        </main></>
                )}
            </IonContent>
<FantasyPlayerModal
    leagueId={id || ""}
    playerId={selectedFantasyPlayerId || ""}
    isOpen={selectedFantasyPlayerId !== null}
    onDismiss={function () {
        setSelectedFantasyPlayerId(null);
    }}
    onDropPlayer={handleDropFromPlayerModal}
/>


<IonAlert
    cssClass="fantasy-drop-alert"
    isOpen={Boolean(selectedPlayer)}

                header="¿Soltar jugador?"
                message={
    selectedPlayer
        ? `Liberarás a <strong>${escapeHtml(
            selectedPlayer.name
        )}</strong>. El jugador volverá a estar disponible para cualquier equipo de la liga.`
        : ""
}

                backdropDismiss={!saving}
                buttons={[
                    {
                        text: "Cancelar",
                        role: "cancel",
                        handler: function () {
                            if (!saving) {
                                setSelectedPlayer(null);
                            }
                        }
                    },
                    {
                        text: saving
                            ? "Liberando..."
                            : "Soltar jugador",
                        role: "destructive",
                        handler: function () {
                            confirmDrop();

                            return false;
                        }
                    }
                ]}
            />

            <IonToast
                isOpen={Boolean(message)}
                message={message}
                duration={3400}
                position="bottom"
                onDidDismiss={function () {
                    setMessage("");
                }}
            />
        </IonPage>
    );
};

export default FantasyDropPlayers;
